"use client"
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  memo,
  useMemo,
  Fragment,
} from "react"
import Content from "@/components/ui/Content"
import {
  TModuleNewsClip,
  TModuleTheList,
  TModuleTheListHeader,
} from "@/types/base"
import { cva, type VariantProps } from "class-variance-authority"
import { ArrowLeft, ArrowRight } from "lucide-react"
import { cn } from "@/lib/utils"
import { MoveUpRight } from "lucide-react"
import moment from "moment"
// TODO: This file should be broken down into smaller components

// Types
type ClipItem = TModuleNewsClip | TModuleTheList
type ClipType = "primary" | "secondary"
type ScrollDirection = "left" | "right"

// Style Variants
const ClipsVariants = cva(
  "p-[var(--temp-spacing-4,16px)] space-y-6 text-left lg:p-4 lg:overflow-auto",
  {
    variants: {
      type: {
        primary:
          "bg-card text-foreground border-8 border-foreground dark:border-border-inverse",
        secondary: "bg-foreground text-background",
      },
    },
    defaultVariants: {
      type: "primary",
    },
  },
)

const scrollShadowVariants = cva("absolute z-10", {
  variants: {
    type: {
      primary: "bg-card",
      secondary: "bg-foreground",
    },
    side: {
      left: "top-0 bottom-0 w-8 -left-0.5 lg:hidden",
      right: "top-0 bottom-0 w-8 -right-0.5 lg:hidden",
      top: "left-0 right-0 h-28 top-0 hidden lg:block",
      bottom: "left-0 right-0 h-28 bottom-0 hidden lg:block",
    },
  },
})

const ClipCardVariants = cva(
  "max-w-[33.33vw] flex-shrink-0 lg:min-w-full transition-transform duration-300",
  {
    variants: {
      type: {
        primary: "",
        secondary: "",
      },
    },
  },
)

const scrollButtonClasses = cva(
  "flex items-center justify-center transition-colors h-8 w-8 p-2.5 rounded-full",
  {
    variants: {
      state: {
        active: "opacity-100 hover:bg-gray-50",
        disabled: "opacity-40 cursor-not-allowed",
      },
    },
    defaultVariants: {
      state: "active",
    },
  },
)

// Debounce utility function
function debounce<T extends (...args: unknown[]) => void>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// Custom Hook for Scroll Logic
function useScrollableContainer() {
  const ref = useRef<HTMLOListElement>(null)
  const [scrollState, setScrollState] = useState({
    showLeftShadow: false,
    showRightShadow: false,
    showTopShadow: false,
    showBottomShadow: false,
    canScrollVertically: false,
    canScrollHorizontally: false,
  })

  const checkScrollPosition = useCallback(() => {
    const container = ref.current
    if (!container) return

    // Horizontal scroll detection (mobile)
    const scrollLeft = container.scrollLeft
    const scrollWidth = container.scrollWidth
    const clientWidth = container.clientWidth
    const maxScrollLeft = scrollWidth - clientWidth
    const canScrollHorizontally = maxScrollLeft > 10

    // Vertical scroll detection (desktop)
    const scrollTop = container.scrollTop
    const scrollHeight = container.scrollHeight
    const clientHeight = container.clientHeight
    const maxScrollTop = scrollHeight - clientHeight
    const canScrollVertically = maxScrollTop > 10

    // Calculate shadow visibility
    setScrollState({
      showLeftShadow: canScrollHorizontally && scrollLeft > 10,
      showRightShadow: canScrollHorizontally && scrollLeft < maxScrollLeft - 10,
      showTopShadow: canScrollVertically && scrollTop > 10,
      showBottomShadow: canScrollVertically && scrollTop < maxScrollTop - 10,
      canScrollVertically,
      canScrollHorizontally,
    })
  }, [])

  const scroll = useCallback((direction: ScrollDirection) => {
    const container = ref.current
    if (!container) return

    const scrollDistance = container.clientWidth * 0.6
    container.scrollTo({
      left:
        direction === "left"
          ? container.scrollLeft - scrollDistance
          : container.scrollLeft + scrollDistance,
      behavior: "smooth",
    })
  }, [])

  const debouncedCheckScroll = useMemo(
    () => debounce(checkScrollPosition, 10),
    [checkScrollPosition],
  )

  useEffect(() => {
    const container = ref.current
    if (!container) return

    const resizeObserver = new ResizeObserver(debouncedCheckScroll)
    resizeObserver.observe(container)

    // Initial check
    checkScrollPosition()

    // Scroll events
    container.addEventListener("scroll", debouncedCheckScroll, {
      passive: true,
    })
    container.addEventListener("touchstart", debouncedCheckScroll, {
      passive: true,
    })
    container.addEventListener("touchend", debouncedCheckScroll, {
      passive: true,
    })

    return () => {
      container.removeEventListener("scroll", debouncedCheckScroll)
      container.removeEventListener("touchstart", debouncedCheckScroll)
      container.removeEventListener("touchend", debouncedCheckScroll)
      resizeObserver.disconnect()
    }
  }, [debouncedCheckScroll, checkScrollPosition])

  return { ref, scrollState, scroll }
}

// Main Component
interface ClipsProps extends VariantProps<typeof ClipsVariants> {
  title: string
  type?: ClipType
  items: ClipItem[]
  theListHeader: TModuleTheListHeader[]
}

export default function Clips({
  title,
  type = "primary",
  items,
  theListHeader,
}: ClipsProps) {
  const { ref, scrollState, scroll } = useScrollableContainer()

  if (!items?.length) return null

  return (
    <section className={ClipsVariants({ type })}>
      <div className="flex lg:flex-col ">
        <h1 className="text-[62px] lg:text-[85px] font-black leading-[100%] tracking-[-.075em] uppercase w-4/6 lg:w-full">
          {title}
        </h1>
        <div className="text-sm flex flex-col lg:flex-row text-secondary-inverse self-end lg:self-start">
          {moment(theListHeader[0].date).format("MMM D, YYYY")}{" "}
          <span className="hidden lg:inline pl-1 pr-1">|</span>
          <span className="flex items-center border-b border-foreground ml-1 w-auto">
            <a
              href={theListHeader[0].url}
              className="font-semibold text-foreground "
              target="_blank"
            >
              Full Edition
            </a>{" "}
            <MoveUpRight className="w-3 h-3 ml-1" />
          </span>
        </div>
      </div>

      <div className="relative">
        {/* Mobile shadows (left/right) */}
        {scrollState.canScrollHorizontally && (
          <>
            <ScrollShadow
              side="left"
              visible={scrollState.showLeftShadow}
              type={type}
            />
            <ScrollShadow
              side="right"
              visible={scrollState.showRightShadow}
              type={type}
            />
          </>
        )}

        {/* Desktop top shadow */}
        {scrollState.canScrollVertically && (
          <div className="sticky top-0 z-10 hidden lg:block">
            <ScrollShadow
              side="top"
              visible={scrollState.showTopShadow}
              type={type}
            />
          </div>
        )}
        {/* Clip list */}
        <ClipList ref={ref} items={items} type={type} />
        {/* Desktop bottom shadow */}
        {scrollState.canScrollVertically && (
          <div className="sticky bottom-0 z-10 hidden lg:block">
            <ScrollShadow
              side="bottom"
              visible={scrollState.showBottomShadow}
              type={type}
            />
          </div>
        )}
        {/* Mobile scroll buttons */}
        {scrollState.canScrollHorizontally && (
          <Fragment>
            <ScrollButton
              direction="left"
              visible={scrollState.showLeftShadow}
              onClick={() => scroll("left")}
            />
            <ScrollButton
              direction="right"
              visible={scrollState.showRightShadow}
              onClick={() => scroll("right")}
            />
          </Fragment>
        )}
      </div>
    </section>
  )
}

// Subcomponents
interface ScrollShadowProps {
  side: "left" | "right" | "top" | "bottom"
  visible: boolean
  type: "primary" | "secondary"
}

const ScrollShadow = memo(({ side, visible, type }: ScrollShadowProps) => {
  if (!visible) return null

  return (
    <div
      className={scrollShadowVariants({ type, side })}
      style={{
        maskImage: `linear-gradient(to ${
          side === "left"
            ? "right"
            : side === "right"
              ? "left"
              : side === "top"
                ? "bottom"
                : "top"
        }, black, transparent)`,
        WebkitMaskImage: `linear-gradient(to ${
          side === "left"
            ? "right"
            : side === "right"
              ? "left"
              : side === "top"
                ? "bottom"
                : "top"
        }, black, transparent)`,
      }}
    />
  )
})
ScrollShadow.displayName = "ScrollShadow"

interface ClipListProps {
  items: ClipItem[]
  type: "primary" | "secondary"
}

const ClipList = React.forwardRef<HTMLOListElement, ClipListProps>(
  ({ items, type }, ref) => (
    <ol
      ref={ref}
      className="flex overflow-x-auto hide-scrollbar gap-10 lg:flex-col lg:space-y-6 scroll-smooth lg:gap-0  lg:overflow-y-auto lg:h-[600px] list-decimal list-inside"
      style={{
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      }}
    >
      {items.map((item, index) => (
        <ClipCard
          key={`${item.slug}-${index}`}
          item={item}
          variant="article"
          type={type}
          index={index}
          totalCount={items.length}
        />
      ))}
    </ol>
  ),
)
ClipList.displayName = "ClipList"

interface ClipCardProps {
  item: ClipItem
  variant: "article" | "card"
  type: "primary" | "secondary"
  index: number
  totalCount: number
}

const ClipCard = memo(({ item, type, index, totalCount }: ClipCardProps) => {
  const paginationStyle = {
    padding: "0 var(--spacing-1-5) var(--spacing-px) var(--spacing-1-5)",
    borderRadius: "var(--border-radius-full)",
    border: "1px solid var(--base-border-c-border-inverse)",
  }

  return (
    <li
      className={cn(
        ClipCardVariants({ type }),
        "flex flex-col justify-between lg:[flex:none] lg:[flex-direction:initial] lg:[justify-content:initial] lg:space-y-none ",
      )}
    >
      <div className="flex flex-row justify-between lg:space-y-none ">
        <p className="text-3xl font-bold pr-2">{index + 1}</p>
        <Content variant="clip" headline={item.headline} type={type} />
      </div>

      <div
        className="text-[10px] gap-[10px] w-max mt-6 lg:hidden"
        style={paginationStyle}
      >
        {index + 1} / {totalCount}
      </div>
    </li>
  )
})
ClipCard.displayName = "ClipCard"

interface ScrollButtonProps {
  direction: ScrollDirection
  visible: boolean
  onClick: () => void
}

const ScrollButton = memo(
  ({ direction, visible, onClick }: ScrollButtonProps) => {
    const Icon = direction === "left" ? ArrowLeft : ArrowRight

    return (
      <div
        className={cn(
          "absolute z-20 lg:hidden",
          "top-1/2 -translate-y-1/2",
          direction === "left" ? "left-[-4px]" : "right-[-4px]",
        )}
      >
        {visible && (
          <button
            onClick={onClick}
            aria-label={`Scroll ${direction}`}
            style={{
              border: "1px solid #E5E5E5",
              background: "#FFF",
              color: "#000",
            }}
            className={scrollButtonClasses({
              state: visible ? "active" : "disabled",
            })}
          >
            <Icon className="w-full h-full text-gray-900" />
          </button>
        )}
      </div>
    )
  },
)
ScrollButton.displayName = "ScrollButton"
